import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Shield, Zap, Target, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import IronDadLogo from '@/components/IronDadLogo';

const Landing = () => {
  const navigate = useNavigate();

  const features = [
    {
      icon: Zap,
      title: 'Gamified Progress',
      description: 'Level up through 155 workouts, earn badges, share your progress and track your transformation'
    },
    {
      icon: Target,
      title: 'Home Workouts',
      description: 'Train anywhere, anytime with curated workout plans designed for dads'
    },
    {
      icon: Shield,
      title: 'Nutrition Guidance',
      description: 'Balanced meal tips to fuel your journey from Dad Bod to Beast'
    },
    {
      icon: Users,
      title: 'Brotherhood Community',
      description: 'Real-time chat with fellow dads on the same mission'
    }
  ];

  return (
    <div className="min-h-screen bg-background noise-bg">
      {/* Hero Section */}
      <div className="relative h-screen flex items-end justify-center overflow-hidden pb-12">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-50"
          style={{ backgroundImage: `url('https://customer-assets.emergentagent.com/job_account-update-5/artifacts/06ytdh46_IMG_5088.jpeg')` }}
        />
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="mb-6 flex justify-center">
              <IronDadLogo size="large" />
            </div>
            <h1 className="text-5xl md:text-7xl font-extrabold tracking-tighter uppercase text-foreground mb-6">
              From Dad Bod
              <br />
              <span className="text-primary">To Beast</span>
            </h1>
            <p className="text-xl md:text-2xl font-semibold text-accent mb-4">
              Become the father your kids look up to
            </p>
            <p className="text-base md:text-lg text-muted-foreground max-w-2xl mx-auto mb-8 leading-relaxed">
              Join the brotherhood. Complete workouts, level up, earn badges. Track your progress at your own pace and transform your body.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                data-testid="get-started-button"
                onClick={() => navigate('/signup')}
                className="bg-primary text-white hover:bg-primary/90 rounded-sm font-bold uppercase tracking-wider h-12 px-8 text-base"
              >
                Get Started
              </Button>
              <Button
                data-testid="login-button"
                onClick={() => navigate('/login')}
                className="bg-secondary text-white hover:bg-secondary/80 rounded-sm font-medium h-12 px-8 text-base"
              >
                Login
              </Button>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Features Section */}
      <div className="py-24 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-5xl font-bold tracking-tight uppercase mb-4">Why Iron Dad?</h2>
          <p className="text-base md:text-lg text-muted-foreground mb-4">Everything you need to transform your body and mindset</p>
          <p className="text-sm md:text-base text-muted-foreground max-w-3xl mx-auto mb-4">
            Designed by a <span className="text-primary font-semibold">Personal Trainer with over 15 years of experience</span>, 
            <span className="text-primary font-semibold"> Gym Owner</span>, and most importantly, a <span className="text-primary font-semibold">Dad</span> who 
            understands the challenges of balancing fitness with family life.
          </p>
          <p className="text-base md:text-lg font-semibold text-accent">
            Simple, Proven & Effective workouts
          </p>
          
          {/* Trainer Photo */}
          <div className="max-w-2xl mx-auto mb-12 mt-8">
            <img 
              src="https://customer-assets.emergentagent.com/job_account-update-5/artifacts/nprg4k8t_IMG_5092.jpeg"
              alt="Fitness trainer - determined and focused"
              className="w-full h-auto rounded-sm border border-border shadow-lg"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {features.map((feature, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              viewport={{ once: true }}
              className="bg-card border border-border rounded-sm p-6 hover:border-primary/50 transition-colors duration-300"
            >
              <feature.icon className="w-12 h-12 text-primary mb-4" />
              <h3 className="text-xl font-semibold mb-3">{feature.title}</h3>
              <p className="text-muted-foreground text-sm leading-relaxed">{feature.description}</p>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Equipment Section */}
      <div className="py-24 px-4 sm:px-6 lg:px-8 bg-card/50">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-5xl font-bold tracking-tight uppercase mb-4">
              Minimal Equipment, Maximum Results
            </h2>
            <p className="text-base md:text-lg text-muted-foreground">
              Low cost, minimal, effective equipment for home workouts
            </p>
          </div>
          
          <div className="bg-card border-2 border-primary/30 rounded-sm p-8">
            <h3 className="text-2xl font-bold text-primary mb-6 text-center uppercase">Equipment You'll Need:</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-start gap-3 bg-background/50 p-4 rounded-sm">
                <div className="text-2xl">🪢</div>
                <div>
                  <h4 className="font-semibold text-lg">Skipping Rope</h4>
                  <p className="text-sm text-muted-foreground">1 x Skipping rope</p>
                </div>
              </div>
              
              <div className="flex items-start gap-3 bg-background/50 p-4 rounded-sm">
                <div className="text-2xl">💪</div>
                <div>
                  <h4 className="font-semibold text-lg">Moderate Dumbbells</h4>
                  <p className="text-sm text-muted-foreground">1 x set (5-8kg)</p>
                </div>
              </div>
              
              <div className="flex items-start gap-3 bg-background/50 p-4 rounded-sm">
                <div className="text-2xl">🏋️</div>
                <div>
                  <h4 className="font-semibold text-lg">Heavy Dumbbells</h4>
                  <p className="text-sm text-muted-foreground">1 x set (10-25kg)</p>
                </div>
              </div>
              
              <div className="flex items-start gap-3 bg-background/50 p-4 rounded-sm">
                <div className="text-2xl">⚫</div>
                <div>
                  <h4 className="font-semibold text-lg">Kettlebells</h4>
                  <p className="text-sm text-muted-foreground">1 x set (10-24kg)</p>
                </div>
              </div>
              
              <div className="flex items-start gap-3 bg-background/50 p-4 rounded-sm md:col-span-2">
                <div className="text-2xl">⚽</div>
                <div>
                  <h4 className="font-semibold text-lg">Medicine Ball</h4>
                  <p className="text-sm text-muted-foreground">1 x Medicine Ball (8-12kg)</p>
                </div>
              </div>
            </div>
            
            <div className="mt-6 text-center">
              <p className="text-sm text-accent font-semibold">
                ✓ Everything you need to complete all 155 workouts from home
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Pricing Section */}
      <div className="py-24 px-4 sm:px-6 lg:px-8 bg-zinc-900/50">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-5xl font-bold tracking-tight uppercase mb-6">
            Transform Your Life
          </h2>
          
          {/* VIP Pre-Sale Pricing */}
          <div className="bg-gradient-to-r from-accent/20 to-primary/20 border-2 border-accent rounded-sm p-6 mb-6">
            <div className="inline-block bg-accent text-black font-bold uppercase text-sm px-4 py-2 rounded-full mb-4">
              🔥 VIP Pre-Sale - Limited Spots
            </div>
            <p className="text-base md:text-lg text-muted-foreground mb-2">
              Special Launch Price
            </p>
            <div className="flex items-center justify-center gap-4 mb-4">
              <span className="text-3xl font-bold text-muted-foreground line-through">$59</span>
              <span className="text-5xl md:text-6xl font-extrabold text-accent">$39</span>
              <span className="text-xl text-muted-foreground">AUD per month</span>
            </div>
            <p className="text-sm text-accent font-semibold mb-2">
              Save $20 AUD/month - Lock in this rate forever!
            </p>
            <p className="text-xs text-muted-foreground">
              Limited spots available
            </p>
          </div>

          {/* Regular Pricing */}
          <p className="text-sm text-muted-foreground mb-8">
            Regular price: $59/month after pre-sale ends. Monthly subscription, cancel anytime.
          </p>
          
          <div className="max-w-2xl mx-auto bg-card border border-border rounded-sm p-8 mb-8">
            <div className="space-y-4 text-left">
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-accent flex items-center justify-center flex-shrink-0 mt-1">
                  <span className="text-black font-bold text-sm">✓</span>
                </div>
                <p className="text-muted-foreground">Unlimited access to all workout programs</p>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-accent flex items-center justify-center flex-shrink-0 mt-1">
                  <span className="text-black font-bold text-sm">✓</span>
                </div>
                <p className="text-muted-foreground">Gamified progress tracking with levels and badges</p>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-accent flex items-center justify-center flex-shrink-0 mt-1">
                  <span className="text-black font-bold text-sm">✓</span>
                </div>
                <p className="text-muted-foreground">Expert nutrition guidance and meal tips</p>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-accent flex items-center justify-center flex-shrink-0 mt-1">
                  <span className="text-black font-bold text-sm">✓</span>
                </div>
                <p className="text-muted-foreground">Real-time community chat with fellow dads</p>
              </div>
            </div>
          </div>
          <Button
            data-testid="pricing-get-started-button"
            onClick={() => navigate('/signup')}
            className="bg-primary text-white hover:bg-primary/90 rounded-sm font-bold uppercase tracking-wider h-12 px-8 text-base"
          >
            Start Your Journey
          </Button>
        </div>
      </div>

      {/* CTA Section */}
      <div className="py-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-5xl font-bold tracking-tight uppercase mb-6">
            Ready To Transform?
          </h2>
          <p className="text-base md:text-lg text-muted-foreground mb-8">
            Join hundreds of dads who are leveling up their fitness game
          </p>
          <Button
            data-testid="cta-get-started-button"
            onClick={() => navigate('/signup')}
            className="bg-primary text-white hover:bg-primary/90 rounded-sm font-bold uppercase tracking-wider h-12 px-8 text-base"
          >
            Start Your Journey
          </Button>
        </div>
      </div>
    </div>
  );
};

export default Landing;
